<template>
  <div
    class="p-6 rounded-xl grid grid-cols-1 justify-between border border-primary-100 gap-4"
    :class="isPaid ? 'md:grid-cols-4' : 'md:grid-cols-3'"
  >
    <div class="flex flex-col md:flex-row gap-4 md:col-span-2">
      <div v-if="isPaid" class="flex flex-col justify-center w-8">
        <BaseIcon
          v-if="isNotActivated"
          name="outline_exclamation_thin"
          class="text-pro-700"
          size="lg"
        />
        <BaseIcon
          v-else
          name="outline_ticket_thin"
          class="text-primary-700"
          size="lg"
        />
      </div>
      <div>
        <div class="font-bold">
          {{ item.details.ticket.name || item.entity_id }}
        </div>
        <template v-if="isPaid">
          <div v-if="isNotActivated" class="text-pro-700">
            {{ $t('tickets.ticket_to_activate') }}
          </div>
          <template v-else>
            <div v-if="item.activation_date" class="flex gap-2">
              {{ $t('tickets.activated') }}:
              {{ getHumanizedDate(item.activation_date) }}
            </div>
            <div>
              {{ $t('tickets.for') }}:
              <span class="text-primary-700">
                {{ item.email }}
              </span>
            </div>
          </template>
        </template>
      </div>
    </div>
    <div class="flex gap-1 items-center whitespace-nowrap">
      <div class="md:hidden">{{ $t('tickets.price') }}:</div>
      <div class="font-bold">
        {{ formatPrice(item.amount / 100, currency, languageMap[locale]) }}
      </div>
    </div>
    <div v-if="isPaid" class="flex items-center md:justify-end pt-2 md:pt-0">
      <BaseLocaleLink v-if="!isNotActivated && eventRoute" :to="eventRoute">
        <BaseButton look="border">
          {{ $t('events.go_to_event') }}
        </BaseButton>
      </BaseLocaleLink>
      <BaseButton
        v-else
        look="solid"
        :class="isUpdating && 'pointer-events-none'"
        :loading="isUpdating"
        @click="isTicketsActivationModalOpen = true"
      >
        {{ $t('subscriptions.activate_ticket') }}
      </BaseButton>
    </div>
    <TicketsActivationModal
      v-if="isTicketsActivationModalOpen"
      :is-open="isTicketsActivationModalOpen"
      :ticket-id="item.id"
      @close="isTicketsActivationModalOpen = false"
      @activated="onActivated"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import type { RouteLocationRaw } from 'vue-router'
import { Currency, OrderItemActivationStatus } from '~/models/orders'
import type { TicketsOrderItemResponse } from '~/models/orders'
import { useOrders } from '~/stores/orders'
import { languageMap } from '~/models/locale'

interface PropsInterface {
  item: TicketsOrderItemResponse
  currency: Currency
  isPaid: boolean
  eventRoute?: string | RouteLocationRaw
  orderId: number
}

const props = withDefaults(defineProps<PropsInterface>(), {
  eventRoute: '',
})

const { locale } = useI18n()
const { getHumanizedDate } = useDate()
const { formatPrice, fetchOrder } = useOrders()

const isTicketsActivationModalOpen = ref(false)
const isUpdating = ref(false)

const isNotActivated = computed(
  () =>
    props.item.activation_status === OrderItemActivationStatus.Created &&
    !props.item.email
)

const onActivated = async () => {
  try {
    isUpdating.value = true
    await fetchOrder(props.orderId, true)
  } finally {
    isUpdating.value = false
  }
}
</script>
